import { useState, useCallback, useRef, useEffect } from "react";
import { Check, Upload, Loader2, ChevronDown, SlidersHorizontal } from "lucide-react";
import { cn } from "@/lib/utils";

/** Iteration data structure matching backend's IterationData. */
export interface IterationData {
  file_path: string;
  old_str: string;
  param_name: string;
  current_value: string;
  value_type: "color" | "number" | "text" | "boolean" | "select";
  unit?: string;
  min_val?: number;
  max_val?: number;
  step?: number;
  options?: string[];
  template: string;
}

interface IterationControlsProps {
  data: IterationData;
  onApply: (newValue: string, updatedData: IterationData) => void;
  onPush?: () => void;
  showPush?: boolean;
  applying?: boolean;
  pushing?: boolean;
  compact?: boolean;
}

/** Inline iteration controls for tweaking a single parameter value. */
export function IterationControls({
  data,
  onApply,
  onPush,
  showPush = false,
  applying = false,
  pushing = false,
  compact = false,
}: IterationControlsProps) {
  const [localValue, setLocalValue] = useState(data.current_value);
  const [hasChanged, setHasChanged] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync when external data changes (after apply)
  useEffect(() => {
    setLocalValue(data.current_value);
    setHasChanged(false);
  }, [data.current_value]);

  const handleChange = useCallback((val: string) => {
    setLocalValue(val);
    setHasChanged(val !== data.current_value);
  }, [data.current_value]);

  const handleApply = useCallback(() => {
    if (!hasChanged || applying) return;
    onApply(localValue, { ...data, current_value: localValue });
  }, [hasChanged, applying, localValue, data, onApply]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Enter" && hasChanged) {
      handleApply();
    }
  }, [hasChanged, handleApply]);

  return (
    <div className={cn("flex flex-col gap-2", compact ? "px-4 py-3" : "p-0")}>
      {/* Input control based on value type */}
      <div className="flex items-center gap-3">
        <label className="text-2xs text-daydra-muted shrink-0 min-w-[60px]">
          {data.param_name}
        </label>
        <div className="flex-1 min-w-0">
          {data.value_type === "number" && (
            <NumberControl
              value={localValue}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              unit={data.unit}
              min={data.min_val}
              max={data.max_val}
              step={data.step}
              inputRef={inputRef}
            />
          )}
          {data.value_type === "color" && (
            <ColorControl
              value={localValue}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              inputRef={inputRef}
            />
          )}
          {data.value_type === "text" && (
            <TextControl
              value={localValue}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              inputRef={inputRef}
            />
          )}
          {data.value_type === "boolean" && (
            <BooleanControl
              value={localValue}
              onChange={handleChange}
              options={data.options}
            />
          )}
          {data.value_type === "select" && (
            <SelectControl
              value={localValue}
              onChange={handleChange}
              options={data.options || []}
            />
          )}
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-2">
        <button
          onClick={handleApply}
          disabled={!hasChanged || applying}
          className={cn(
            "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200",
            hasChanged && !applying
              ? "bg-daydra-accent/20 text-daydra-accent border border-daydra-accent/30 hover:bg-daydra-accent/30 cursor-pointer"
              : "bg-white/[0.04] text-daydra-muted/40 border border-white/[0.06] cursor-default",
          )}
        >
          {applying ? (
            <Loader2 size={12} className="animate-spin" />
          ) : (
            <Check size={12} />
          )}
          Apply
        </button>

        {showPush && onPush && (
          <button
            onClick={onPush}
            disabled={pushing}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-daydra-success/[0.12] text-daydra-success border border-daydra-success/20 hover:bg-daydra-success/20 transition-all duration-200 cursor-pointer"
          >
            {pushing ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <Upload size={12} />
            )}
            Commit & Push
          </button>
        )}
      </div>
    </div>
  );
}

/** Collapsible wrapper for the WorkProgressCard version. */
export function CollapsibleIteration({
  data,
  onApply,
  onPush,
  showPush = false,
  applying = false,
  pushing = false,
}: IterationControlsProps) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border-t border-white/[0.06]">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-4 py-2.5 text-xs text-daydra-muted hover:text-daydra-accent transition-colors cursor-pointer"
      >
        <SlidersHorizontal size={12} className="shrink-0" />
        <span className="flex-1 text-left">Adjust {data.param_name}</span>
        <ChevronDown
          size={14}
          className={cn(
            "shrink-0 transition-transform duration-300",
            !open && "-rotate-90",
          )}
        />
      </button>

      <div
        className="grid transition-[grid-template-rows] duration-300 ease-in-out"
        style={{ gridTemplateRows: open ? "1fr" : "0fr" }}
      >
        <div className="overflow-hidden">
          <div className="border-t border-white/[0.04]">
            <IterationControls
              data={data}
              onApply={onApply}
              onPush={onPush}
              showPush={showPush}
              applying={applying}
              pushing={pushing}
              compact
            />
          </div>
        </div>
      </div>
    </div>
  );
}


/* ── Input controls ─────────────────────────────────────────── */

function NumberControl({
  value,
  onChange,
  onKeyDown,
  unit,
  min,
  max,
  step,
  inputRef,
}: {
  value: string;
  onChange: (v: string) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  unit?: string;
  min?: number;
  max?: number;
  step?: number;
  inputRef: React.RefObject<HTMLInputElement | null>;
}) {
  const numVal = parseFloat(value) || 0;

  return (
    <div className="flex items-center gap-2">
      <input
        type="range"
        min={min ?? 0}
        max={max ?? 500}
        step={step ?? 1}
        value={numVal}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 h-1.5 rounded-full appearance-none cursor-pointer
          bg-white/[0.08] accent-daydra-accent
          [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3
          [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:appearance-none
          [&::-webkit-slider-thumb]:bg-daydra-accent [&::-webkit-slider-thumb]:shadow-md
          [&::-webkit-slider-thumb]:cursor-pointer"
      />
      <div className="flex items-center gap-0.5 shrink-0">
        <input
          ref={inputRef}
          type="number"
          value={value}
          min={min}
          max={max}
          step={step}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          className="w-16 h-7 px-2 rounded-md bg-daydra-raised border border-daydra-border text-xs text-daydra-text text-center
            focus:outline-none focus:border-daydra-accent/50 [appearance:textfield]
            [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        />
        {unit && (
          <span className="text-2xs text-daydra-muted">{unit}</span>
        )}
      </div>
    </div>
  );
}

function ColorControl({
  value,
  onChange,
  onKeyDown,
  inputRef,
}: {
  value: string;
  onChange: (v: string) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
}) {
  // Normalize to hex for color picker
  const hexVal = value.startsWith("#") ? value : "#000000";

  return (
    <div className="flex items-center gap-2">
      <div className="relative shrink-0">
        <input
          type="color"
          value={hexVal}
          onChange={(e) => onChange(e.target.value)}
          className="w-8 h-8 rounded-md cursor-pointer border border-daydra-border bg-transparent
            [&::-webkit-color-swatch-wrapper]:p-0.5
            [&::-webkit-color-swatch]:rounded-[4px] [&::-webkit-color-swatch]:border-none"
        />
      </div>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        className="flex-1 h-7 px-2 rounded-md bg-daydra-raised border border-daydra-border text-xs text-daydra-text font-mono
          focus:outline-none focus:border-daydra-accent/50"
      />
      <div
        className="w-6 h-6 rounded-md border border-white/10 shrink-0"
        style={{ backgroundColor: value }}
      />
    </div>
  );
}

function TextControl({
  value,
  onChange,
  onKeyDown,
  inputRef,
}: {
  value: string;
  onChange: (v: string) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
}) {
  return (
    <input
      ref={inputRef}
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={onKeyDown}
      className="w-full h-7 px-2 rounded-md bg-daydra-raised border border-daydra-border text-xs text-daydra-text
        focus:outline-none focus:border-daydra-accent/50"
    />
  );
}

function BooleanControl({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options?: string[];
}) {
  const opts = options || ["true", "false"];
  const isFirst = value.toLowerCase() === opts[0]?.toLowerCase();

  return (
    <button
      onClick={() => onChange(isFirst ? opts[1]! : opts[0]!)}
      className="relative flex items-center w-10 h-5 rounded-full transition-colors duration-200 cursor-pointer"
      style={{ backgroundColor: isFirst ? "rgba(232,164,74,0.3)" : "rgba(255,255,255,0.08)" }}
    >
      <div
        className={cn(
          "absolute w-3.5 h-3.5 rounded-full transition-all duration-200",
          isFirst ? "left-5 bg-daydra-accent" : "left-1 bg-daydra-muted/60",
        )}
      />
      <span className="ml-12 text-2xs text-daydra-muted whitespace-nowrap">
        {value}
      </span>
    </button>
  );
}

function SelectControl({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: string[];
}) {
  // Use radio buttons for 4 or fewer options, otherwise a dropdown
  if (options.length <= 4) {
    return (
      <div className="flex flex-wrap gap-1.5">
        {options.map((opt) => (
          <button
            key={opt}
            onClick={() => onChange(opt)}
            className={cn(
              "px-2.5 py-1 rounded-md text-xs transition-all duration-200 cursor-pointer",
              opt === value
                ? "bg-daydra-accent/20 text-daydra-accent border border-daydra-accent/30"
                : "bg-white/[0.04] text-daydra-muted border border-white/[0.06] hover:border-white/[0.12] hover:text-daydra-text",
            )}
          >
            {opt}
          </button>
        ))}
      </div>
    );
  }

  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full h-7 px-2 rounded-md bg-daydra-raised border border-daydra-border text-xs text-daydra-text
        focus:outline-none focus:border-daydra-accent/50 cursor-pointer"
    >
      {options.map((opt) => (
        <option key={opt} value={opt}>{opt}</option>
      ))}
    </select>
  );
}
